import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Plus, ChevronLeft, X, Info, MessageSquare, Hash, Users, Settings2, Command,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@easyflow/ui-frontend/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { cn } from '@/lib/utils';
import { useDensity } from '@/components/aurora/useDensity';
import { useRegisterShortcuts } from '@/components/aurora/ShortcutsHelp';

import { messengerApi } from './lib/messengerApi';
import type { ChatDensity, ChatMessage, ConversationSummary, ParticipantBrief, PresenceStatus } from './types';
import { MessageList } from './components/MessageList';
import { MessageComposer, type ComposerHandle } from './components/MessageComposer';
import { PresenceDot } from './components/PresenceDot';
import { PresenceMenu } from './components/PresenceMenu';
import { QuickSwitcher } from './components/QuickSwitcher';
import { InfoPanel } from './components/InfoPanel';
import { EmptyState } from '@/components/aurora/EmptyState';
import { AuroraGlassPanel } from '@easyflow/ui-frontend';
import { ForwardDialog } from './components/ForwardDialog';
import { CreateTaskDialog } from './components/CreateTaskDialog';
import { usePresence } from './hooks/usePresence';
import { useMessengerShortcuts } from './hooks/useMessengerShortcuts';
import { formatTime, getAvatarGradient, getDraft, getInitials } from './utils/format';

// Task #1660 — Messenger v2: refactor del monolite (1395 LOC) in componenti
// modulari. Questo file resta il "shell" che orchestrera elenco/header/info,
// delegando rendering messaggi, composer, picker mention, ⌘K, presence,
// link preview, voice e edit history ai componenti dedicati in
// `./components/`.
//
// Tutte le chiamate REST passano per `messengerApi` (wrapper su `apiClient`)
// — no `fetch()` diretti. Le route messenger sono whitelisted in
// `TENANT_AGNOSTIC_PREFIXES` (api.ts) per le nuove rotte tenant-from-JWT;
// le storiche restano in dual-mount per back-compat.

// Task #1665 — density unificata col Feed via `useDensity` (chiave
// `aurora_ui_density`). La vecchia chiave `messenger_density` è stata rimossa.
const SEND_HOTKEY_KEY = 'messenger_send_hotkey';
const CHAT_BG_KEY = 'messenger_chat_bg';

export default function MessengerChatDesktop() {
  const { user } = useAuth();
  const { socket, joinRoom, leaveRoom, on, off, socketVersion } = useSocket();

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedConv, setSelectedConv] = useState<ConversationSummary | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'unread' | 'direct' | 'group' | 'channel'>('all');
  const [tenantUsers, setTenantUsers] = useState<ParticipantBrief[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showMobile, setShowMobile] = useState<'list' | 'chat'>('list');
  const [showSettings, setShowSettings] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [forwardMsg, setForwardMsg] = useState<ChatMessage | null>(null);
  const [createTaskMsg, setCreateTaskMsg] = useState<ChatMessage | null>(null);
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({});

  // Density condivisa cross-modulo (Chat + Feed) via hook Aurora.
  const { density, setDensity: setAuroraDensity } = useDensity();
  const [sendHotkey, setSendHotkey] = useState<'enter' | 'ctrl_enter'>(() => {
    try { return (localStorage.getItem(SEND_HOTKEY_KEY) as 'enter' | 'ctrl_enter') || 'enter'; } catch { return 'enter'; }
  });
  const [chatBg, setChatBg] = useState<string>(() => {
    try { return localStorage.getItem(CHAT_BG_KEY) || ''; } catch { return ''; }
  });

  const composerRef = useRef<ComposerHandle | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tenantId = user?.tenant_id || null;
  const userId = user?.id || null;
  const { presenceMap, myStatus, setMyStatus } = usePresence(tenantId);

  // ─── Load conversations ────────────────────────────────────────────────

  const loadConversations = useCallback(async () => {
    const res = await messengerApi.listConversations({
      type: filterType,
      search: searchQuery || undefined,
    });
    if (!res.success || !res.data) return;
    let list = res.data;
    if (filterType === 'unread') list = list.filter((c) => c.unread_count > 0);
    setConversations(list);
  }, [filterType, searchQuery]);

  useEffect(() => { void loadConversations(); }, [loadConversations]);

  const debouncedReloadRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedReload = useCallback(() => {
    if (debouncedReloadRef.current) clearTimeout(debouncedReloadRef.current);
    debouncedReloadRef.current = setTimeout(() => { void loadConversations(); }, 300);
  }, [loadConversations]);

  // ─── Socket listeners (chat events) ────────────────────────────────────

  useEffect(() => {
    const handleNewMsg = (msg: ChatMessage) => {
      if (selectedConv && msg.conversation_id === selectedConv.id) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        void messengerApi.markRead(selectedConv.id);
      }
      debouncedReload();
      window.dispatchEvent(new Event('messenger:new-message'));
    };
    const handleTyping = (data: { conversationId: string; userId: string; userName: string; isTyping: boolean }) => {
      if (data.conversationId !== selectedConv?.id) return;
      setTypingUsers((prev) => {
        const n = { ...prev };
        if (data.isTyping) n[data.userId] = data.userName;
        else delete n[data.userId];
        return n;
      });
    };
    const handleEdited = (msg: ChatMessage) => {
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)));
    };
    const handleDeleted = (data: { messageId: string }) => {
      setMessages((prev) => prev.filter((m) => m.id !== data.messageId));
    };
    const handleReaction = (data: { messageId: string; userId: string; emoji: string; action: 'add' | 'remove' }) => {
      setMessages((prev) => prev.map((m) => {
        if (m.id !== data.messageId) return m;
        const reactions = m.reactions ? [...m.reactions] : [];
        if (data.action === 'add') {
          reactions.push({ emoji: data.emoji, user_id: data.userId });
        } else {
          const idx = reactions.findIndex((r) => r.emoji === data.emoji && r.user_id === data.userId);
          if (idx >= 0) reactions.splice(idx, 1);
        }
        return { ...m, reactions };
      }));
    };
    const handleRead = () => debouncedReload();
    const handleUnread = () => debouncedReload();

    on('chat:message', handleNewMsg);
    on('chat:typing', handleTyping);
    on('chat:message:edited', handleEdited);
    on('chat:message:deleted', handleDeleted);
    on('chat:reaction', handleReaction);
    on('chat:read', handleRead);
    on('chat:unread', handleUnread);
    return () => {
      off('chat:message', handleNewMsg);
      off('chat:typing', handleTyping);
      off('chat:message:edited', handleEdited);
      off('chat:message:deleted', handleDeleted);
      off('chat:reaction', handleReaction);
      off('chat:read', handleRead);
      off('chat:unread', handleUnread);
    };
  }, [selectedConv, on, off, debouncedReload]);

  // ─── Self-healing: riconciliazione autoritativa al (ri)connect ─────────
  // Il pub/sub Redis dell'adapter è fire-and-forget: ogni emit cross-nodo
  // pubblicata mentre il subscriber di un worker è in reconnect viene persa.
  // Al ripristino del socket (`socketVersion`++) i listener ri-sottoscrivono
  // solo eventi FUTURI; senza un refetch lo stato perso (badge non letti +
  // spunte `read_by_count`) resta stale finché non si ricarica o riseleziona.
  // Refs per leggere i valori correnti senza ritriggerare l'effetto: deve
  // scattare SOLO sul cambio di socketVersion, non a ogni cambio conversazione.
  const selectedConvRef = useRef<ConversationSummary | null>(null);
  useEffect(() => { selectedConvRef.current = selectedConv; }, [selectedConv]);
  const loadConversationsRef = useRef(loadConversations);
  useEffect(() => { loadConversationsRef.current = loadConversations; }, [loadConversations]);

  useEffect(() => {
    // socketVersion parte da 0 al mount (load iniziale già gestito altrove);
    // si incrementa solo alle riconnessioni → reconcile solo dopo un buco.
    if (socketVersion === 0) return;
    void loadConversationsRef.current();
    const conv = selectedConvRef.current;
    if (!conv) return;
    void messengerApi.getMessages(conv.id, undefined, 50).then((res) => {
      if (res.success && res.data) {
        setMessages([...res.data].reverse());
      }
      void messengerApi.markRead(conv.id);
    });
  }, [socketVersion]);

  // ─── Conversation selection ────────────────────────────────────────────

  const selectConversation = useCallback(async (convOrId: ConversationSummary | string) => {
    let conv: ConversationSummary | null = null;
    if (typeof convOrId === 'string') {
      conv = conversations.find((c) => c.id === convOrId) || null;
      if (!conv) {
        // Fallback: l'id arriva dal quick switcher e potrebbe non essere
        // ancora in lista (recente cross-tenant). Skip soft.
        return;
      }
    } else {
      conv = convOrId;
    }
    if (selectedConv?.id) leaveRoom(`chat:${selectedConv.id}`);
    setSelectedConv(conv);
    setLoadingMessages(true);
    setHasMore(true);
    setShowMobile('chat');
    joinRoom(`chat:${conv.id}`);

    const res = await messengerApi.getMessages(conv.id, undefined, 50);
    if (res.success && res.data) {
      const msgs = [...res.data].reverse();
      setMessages(msgs);
      setHasMore(msgs.length >= 50);
    }
    setLoadingMessages(false);
    const draft = getDraft(conv.id);
    composerRef.current?.setText(draft);
    void messengerApi.markRead(conv.id).then(() => {
      setConversations((prev) => prev.map((c) => (c.id === conv?.id ? { ...c, unread_count: 0 } : c)));
    });
  }, [conversations, selectedConv, joinRoom, leaveRoom]);

  const loadMoreMessages = useCallback(async () => {
    if (!selectedConv || loadingMore || !hasMore || messages.length === 0) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoadingMore(true);
    const res = await messengerApi.getMessages(selectedConv.id, oldest.created_at, 50);
    if (res.success && res.data) {
      const older = [...res.data].reverse();
      if (older.length < 50) setHasMore(false);
      if (older.length > 0) setMessages((prev) => [...older, ...prev]);
    }
    setLoadingMore(false);
  }, [selectedConv, loadingMore, hasMore, messages]);

  // ─── Send / file / voice ───────────────────────────────────────────────

  const onTyping = useCallback(() => {
    if (!selectedConv || !socket) return;
    socket.emit('chat:typing', { conversationId: selectedConv.id, isTyping: true });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('chat:typing', { conversationId: selectedConv.id, isTyping: false });
    }, 2000);
  }, [selectedConv, socket]);

  const sendText = useCallback(async (content: string, replyToId?: string) => {
    if (!selectedConv) return;
    const res = await messengerApi.sendMessage(selectedConv.id, {
      content,
      reply_to_id: replyToId,
    });
    if (res.success && res.data) {
      setMessages((prev) => (prev.some((m) => m.id === res.data!.id) ? prev : [...prev, res.data!]));
      setReplyTo(null);
    }
  }, [selectedConv]);

  const sendFiles = useCallback(async (files: File[], content?: string) => {
    if (!selectedConv) return;
    const res = await messengerApi.sendFiles(selectedConv.id, files, content);
    if (res.success && res.data) {
      setMessages((prev) => (prev.some((m) => m.id === res.data!.id) ? prev : [...prev, res.data!]));
    }
  }, [selectedConv]);

  const sendVoice = useCallback(async (blob: Blob, durationMs: number) => {
    if (!selectedConv) return;
    const res = await messengerApi.sendVoice(selectedConv.id, blob, durationMs);
    if (res.success && res.data) {
      setMessages((prev) => (prev.some((m) => m.id === res.data!.id) ? prev : [...prev, res.data!]));
    }
  }, [selectedConv]);

  // ─── Reactions / pin / favorite / delete / forward / task ──────────────

  const toggleReaction = useCallback(async (msgId: string, emoji: string) => {
    const msg = messages.find((m) => m.id === msgId);
    const hasReacted = msg?.reactions?.some((r) => r.emoji === emoji && r.user_id === userId);
    if (hasReacted) await messengerApi.removeReaction(msgId, emoji);
    else await messengerApi.addReaction(msgId, emoji);
  }, [messages, userId]);

  const togglePin = useCallback(async (msgId: string, isPinned: boolean) => {
    await messengerApi.togglePin(msgId, isPinned);
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, is_pinned: !isPinned } : m)));
  }, []);

  const toggleFavorite = useCallback(async (msgId: string, isFav: boolean) => {
    await messengerApi.toggleFavorite(msgId, isFav);
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, is_favorited: !isFav } : m)));
  }, []);

  const onDelete = useCallback(async (msgId: string) => {
    await messengerApi.deleteMessage(msgId);
    setMessages((prev) => prev.filter((m) => m.id !== msgId));
  }, []);

  const onForward = useCallback((m: ChatMessage) => setForwardMsg(m), []);
  const onCreateTask = useCallback((m: ChatMessage) => setCreateTaskMsg(m), []);

  // ─── Users / new chat ──────────────────────────────────────────────────

  const searchUsers = useCallback(async (q: string) => {
    setUserSearch(q);
    const res = await messengerApi.searchUsers(q);
    if (res.success && res.data) setTenantUsers(res.data);
  }, []);

  const startDirectChat = useCallback(async (target: ParticipantBrief) => {
    const res = await messengerApi.createDirect(target.id);
    if (res.success && res.data) {
      setShowNewChat(false);
      await loadConversations();
      void selectConversation(res.data);
    }
  }, [loadConversations, selectConversation]);

  // ─── Shortcuts ⌘K / e / r / Esc / ↑ ────────────────────────────────────

  useMessengerShortcuts({
    onQuickSwitcher: () => setShowSwitcher(true),
    onFocusComposer: () => composerRef.current?.focus(),
    onReplyLast: () => {
      const last = [...messages].reverse().find((m) => m.sender_id !== userId);
      if (last) setReplyTo(last);
    },
    onJumpUp: () => {
      const composerText = composerRef.current?.getText() || '';
      if (composerText.trim()) return; // l'utente sta scrivendo, non interferire
      const lastMine = [...messages].reverse().find((m) => m.sender_id === userId);
      if (lastMine && lastMine.content) {
        // Modalità edit semplificata: pre-popola il composer; il submit via
        // patch è esposto come follow-up nell'editor inline avanzato.
        composerRef.current?.setText(lastMine.content);
      }
    },
    onEscape: () => {
      setReplyTo(null);
      setShowSwitcher(false);
    },
  });

  // ─── Header presence + typing display ─────────────────────────────────

  const typingDisplay = Object.values(typingUsers).filter(Boolean);
  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

  const saveDensity = (d: ChatDensity) => setAuroraDensity(d);

  // Task #1665 — registra le scorciatoie Messenger nel modal "?" globale.
  useRegisterShortcuts('messenger', [
    { keys: 'Cmd/Ctrl+K', description: 'Apri Quick Switcher conversazioni', group: 'Messenger' },
    { keys: 'E',          description: 'Focus sul composer messaggi',       group: 'Messenger' },
    { keys: 'R',          description: 'Rispondi all’ultimo messaggio',  group: 'Messenger' },
    { keys: '↑',     description: 'Scorri verso messaggi più vecchi',group: 'Messenger' },
    { keys: '?',          description: 'Mostra/chiudi questa finestra',     group: 'Messenger' },
  ]);
  const saveSendHotkey = (h: 'enter' | 'ctrl_enter') => {
    setSendHotkey(h);
    try { localStorage.setItem(SEND_HOTKEY_KEY, h); } catch { /* noop */ }
  };
  const saveChatBg = (bg: string) => {
    setChatBg(bg);
    try { localStorage.setItem(CHAT_BG_KEY, bg); } catch { /* noop */ }
  };

  const otherDirectStatus = useMemo<PresenceStatus | null>(() => {
    if (!selectedConv || selectedConv.type !== 'direct') return null;
    const other = selectedConv.other_participants?.[0];
    if (!other) return null;
    return presenceMap[other.id] || 'offline';
  }, [selectedConv, presenceMap]);

  const conversationParticipants: ParticipantBrief[] = selectedConv?.other_participants || [];

  const BG_PRESETS = ['', '#f0f9ff', '#f0fdf4', '#fefce8', '#fdf2f8', '#faf5ff', '#f1f5f9', '#ecfeff', '#fff7ed', '#f5f3ff'];

  return (
    <div className="flex h-full overflow-hidden bg-gray-50 dark:bg-gray-950">
      {/* Lista conversazioni */}
      <div className={cn(
        'w-full md:w-80 lg:w-96 border-r flex flex-col bg-white dark:bg-gray-900',
        showMobile === 'chat' && 'hidden md:flex',
      )}>
        <AuroraGlassPanel as="div" intensity="medium" className="p-3 border-b space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold">Chat</h2>
              {totalUnread > 0 && (
                <span className="h-5 min-w-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <PresenceMenu current={myStatus} onChange={setMyStatus} />
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => setShowSwitcher(true)} title="Cerca (⌘K)">
                <Command className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => setShowSettings(true)}>
                <Settings2 className="h-4 w-4" />
              </Button>
              <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
                <DialogTrigger asChild>
                  <Button size="sm" className="h-8 rounded-lg bg-blue-600 hover:bg-blue-700 gap-1" onClick={() => searchUsers('')}>
                    <Plus className="h-3.5 w-3.5" /> Nuova
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Nuova conversazione</DialogTitle></DialogHeader>
                  <Input placeholder="Cerca utente…" value={userSearch} onChange={(e) => searchUsers(e.target.value)} className="rounded-lg" />
                  <ScrollArea className="h-64 mt-2">
                    {tenantUsers.filter((u) => u.id !== userId).map((u) => {
                      const uName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                      const status = presenceMap[u.id] || null;
                      return (
                        <div key={u.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-accent cursor-pointer transition-colors" onClick={() => startDirectChat(u)}>
                          <div className="relative">
                            <Avatar className="h-9 w-9 ring-1 ring-white dark:ring-gray-800">
                              <AvatarImage src={u.avatar_url || undefined} />
                              <AvatarFallback className={`bg-gradient-to-br ${getAvatarGradient(uName)} text-white text-xs font-semibold`}>
                                {getInitials(u.first_name, u.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <PresenceDot status={status} />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{u.first_name} {u.last_name}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </div>
                        </div>
                      );
                    })}
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca conversazioni…"
              className="pl-8 h-9 rounded-lg bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Tabs value={filterType} onValueChange={(v) => setFilterType(v as typeof filterType)}>
            <TabsList className="w-full h-8 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
              <TabsTrigger value="all" className="text-[10px] flex-1 px-1 rounded-md">Tutte</TabsTrigger>
              <TabsTrigger value="unread" className="text-[10px] flex-1 px-1 rounded-md">Non lette</TabsTrigger>
              <TabsTrigger value="direct" className="text-[10px] flex-1 px-1 rounded-md">Dirette</TabsTrigger>
              <TabsTrigger value="group" className="text-[10px] flex-1 px-1 rounded-md">Gruppi</TabsTrigger>
              <TabsTrigger value="channel" className="text-[10px] flex-1 px-1 rounded-md">Canali</TabsTrigger>
            </TabsList>
          </Tabs>
        </AuroraGlassPanel>

        <ScrollArea className="flex-1">
          {conversations.length === 0 && (
            <EmptyState
              compact
              illustration="no-conversations"
              title="Nessuna conversazione"
              subtitle="Avvia una nuova chat con i colleghi o crea un gruppo per iniziare a parlare."
            />
          )}
          {conversations.map((conv) => {
            const isSelected = selectedConv?.id === conv.id;
            const otherUser = conv.other_participants?.[0];
            const otherStatus = otherUser ? (presenceMap[otherUser.id] || null) : null;
            const displayName = conv.display_title || 'Conversazione';
            const isChannel = conv.type === 'channel';
            const isGroup = conv.type === 'group';
            const draft = !isSelected ? getDraft(conv.id) : '';
            return (
              <div
                key={conv.id}
                className={cn(
                  'flex items-center gap-3 px-3 py-3 cursor-pointer transition-all duration-150',
                  isSelected ? 'bg-blue-50 dark:bg-blue-950/30 border-l-3 border-l-blue-500'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
                )}
                onClick={() => selectConversation(conv)}
              >
                <div className="relative flex-shrink-0">
                  <Avatar className="h-11 w-11 ring-2 ring-white dark:ring-gray-800 shadow-sm">
                    <AvatarImage src={conv.avatar_url || otherUser?.avatar_url || undefined} />
                    <AvatarFallback className={cn(
                      'font-semibold text-sm',
                      isChannel ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white'
                                : isGroup ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
                                          : `bg-gradient-to-br ${getAvatarGradient(displayName)} text-white`,
                    )}>
                      {isChannel ? '#' : getInitials(displayName.split(' ')[0], displayName.split(' ')[1])}
                    </AvatarFallback>
                  </Avatar>
                  {conv.type === 'direct' && otherStatus && <PresenceDot status={otherStatus} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isChannel && <Hash className="h-3.5 w-3.5 text-purple-500 shrink-0" />}
                      {isGroup && <Users className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                      <span className={cn('text-sm truncate', isSelected ? 'font-bold' : 'font-medium')}>{displayName}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
                      {formatTime(conv.last_message_at ?? conv.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-muted-foreground truncate pr-2">
                      {draft ? <span className="text-orange-500 italic">Bozza: {draft.substring(0, 30)}</span>
                             : (conv.last_message_preview || conv.last_message || 'Nessun messaggio')}
                    </span>
                    {conv.unread_count > 0 && (
                      <span className="h-5 min-w-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0 shadow-sm">
                        {conv.unread_count > 99 ? '99+' : conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </ScrollArea>
      </div>

      {/* Pannello chat */}
      <div className={cn(
        'flex-1 flex flex-col bg-white dark:bg-gray-900',
        showMobile === 'list' && 'hidden md:flex',
      )}>
        {selectedConv ? (
          <>
            <div className="border-b bg-white dark:bg-gray-900 px-4 py-2.5 flex items-center gap-3 shadow-sm">
              <Button variant="ghost" size="icon" className="md:hidden h-8 w-8 rounded-lg" onClick={() => setShowMobile('list')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="relative">
                <Avatar className="h-9 w-9 ring-1 ring-white dark:ring-gray-800 shadow-sm">
                  <AvatarImage src={selectedConv.avatar_url || selectedConv.other_participants?.[0]?.avatar_url || undefined} />
                  <AvatarFallback className={cn(
                    'text-white font-semibold text-xs',
                    selectedConv.type === 'channel' ? 'bg-gradient-to-br from-purple-500 to-indigo-600'
                                                    : `bg-gradient-to-br ${getAvatarGradient(selectedConv.display_title || '')}`,
                  )}>
                    {selectedConv.type === 'channel' ? '#' : getInitials(selectedConv.display_title?.split(' ')[0], selectedConv.display_title?.split(' ')[1])}
                  </AvatarFallback>
                </Avatar>
                {selectedConv.type === 'direct' && otherDirectStatus && <PresenceDot status={otherDirectStatus} />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm truncate">{selectedConv.display_title}</h3>
                <div className="flex items-center gap-1.5">
                  {typingDisplay.length > 0 ? (
                    <span className="text-xs text-blue-500 font-medium">
                      {typingDisplay.join(', ')} sta scrivendo…
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {selectedConv.type === 'direct'
                        ? (otherDirectStatus === 'online' ? 'Online'
                            : otherDirectStatus === 'away' ? 'Assente'
                            : otherDirectStatus === 'in-call' ? 'In chiamata'
                            : otherDirectStatus === 'dnd' ? 'Non disturbare'
                            : 'Offline')
                        : `${selectedConv.participant_count || 0} membri`}
                    </span>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => setShowInfo(!showInfo)}>
                <Info className="h-4 w-4" />
              </Button>
            </div>

            <div
              className="flex-1 flex flex-col overflow-hidden"
              style={chatBg ? { backgroundColor: chatBg } : undefined}
            >
              <MessageList
                messages={messages}
                loading={loadingMessages}
                loadingMore={loadingMore}
                hasMore={hasMore}
                density={density}
                isGroup={selectedConv.type !== 'direct'}
                currentUserId={userId}
                onLoadMore={loadMoreMessages}
                onReply={setReplyTo}
                onForward={onForward}
                onReact={toggleReaction}
                onPin={togglePin}
                onFavorite={toggleFavorite}
                onCreateTask={onCreateTask}
                onDelete={onDelete}
              />
            </div>

            <MessageComposer
              ref={composerRef}
              conversationId={selectedConv.id}
              participants={conversationParticipants}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              onSendText={sendText}
              onSendFiles={sendFiles}
              onSendVoice={sendVoice}
              onTyping={onTyping}
              sendHotkey={sendHotkey}
            />
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 flex items-center justify-center">
              <MessageSquare className="h-10 w-10 text-blue-300 dark:text-blue-700" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-700 dark:text-gray-300">Seleziona una conversazione</h3>
              <p className="text-sm text-gray-400 mt-1">o premi <kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[10px]">⌘K</kbd> per cercare</p>
            </div>
          </div>
        )}
      </div>

      {/* QuickSwitcher ⌘K */}
      <QuickSwitcher
        open={showSwitcher}
        onClose={() => setShowSwitcher(false)}
        onPickConversation={(id) => { setShowSwitcher(false); void selectConversation(id); }}
        onPickUser={(u) => { setShowSwitcher(false); void startDirectChat(u); }}
      />

      {/* Settings */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent>
          <DialogHeader><DialogTitle>Impostazioni Chat</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Densità lista messaggi</Label>
              <div className="flex gap-2 mt-2">
                <Button variant={density === 'comfortable' ? 'default' : 'outline'} size="sm" className="rounded-lg" onClick={() => saveDensity('comfortable')}>
                  Confortevole
                </Button>
                <Button variant={density === 'compact' ? 'default' : 'outline'} size="sm" className="rounded-lg" onClick={() => saveDensity('compact')}>
                  Compatta
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Invio messaggio</Label>
              <div className="flex gap-2 mt-2">
                <Button variant={sendHotkey === 'enter' ? 'default' : 'outline'} size="sm" className="rounded-lg" onClick={() => saveSendHotkey('enter')}>
                  Enter
                </Button>
                <Button variant={sendHotkey === 'ctrl_enter' ? 'default' : 'outline'} size="sm" className="rounded-lg" onClick={() => saveSendHotkey('ctrl_enter')}>
                  Ctrl+Enter
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {sendHotkey === 'enter' ? 'Enter invia, Shift+Enter a capo' : 'Ctrl+Enter invia, Enter a capo'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">Sfondo chat</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {BG_PRESETS.map((bg, i) => (
                  <button
                    key={i}
                    className={cn(
                      'h-8 w-8 rounded-lg border-2 transition-all',
                      chatBg === bg ? 'border-blue-500 ring-2 ring-blue-500/30 scale-110' : 'border-gray-200 dark:border-gray-700',
                      !bg && 'bg-white dark:bg-gray-900',
                    )}
                    style={bg ? { backgroundColor: bg } : undefined}
                    onClick={() => saveChatBg(bg)}
                    aria-label={`Sfondo ${bg || 'predefinito'}`}
                  />
                ))}
              </div>
            </div>
            <div className="pt-2 border-t">
              <Label className="text-sm font-medium">Scorciatoie</Label>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <span className="text-muted-foreground">⌘K / Ctrl+K</span><span>Cerca conversazioni</span>
                <span className="text-muted-foreground">e</span><span>Focus composer</span>
                <span className="text-muted-foreground">r</span><span>Rispondi ultimo</span>
                <span className="text-muted-foreground">↑ (vuoto)</span><span>Modifica ultimo mio</span>
                <span className="text-muted-foreground">Esc</span><span>Chiudi popup</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setShowSettings(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info pane (Members/Pinned/Favorites/Media) */}
      {showInfo && selectedConv && (
        <InfoPanel
          conversation={selectedConv}
          participants={conversationParticipants}
          presenceMap={presenceMap as Record<string, PresenceStatus>}
          onClose={() => setShowInfo(false)}
        />
      )}

      {/* Forward */}
      <ForwardDialog
        message={forwardMsg}
        conversations={conversations}
        currentConversationId={selectedConv?.id || null}
        onClose={() => setForwardMsg(null)}
      />

      {/* Create Task */}
      <CreateTaskDialog
        message={createTaskMsg}
        participants={conversationParticipants}
        onClose={() => setCreateTaskMsg(null)}
      />
    </div>
  );
}
